import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
  HttpQueryParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { PublicBoxResponse } from '../responses/public-box.response';
import { PublicBoxVisibilityResponse } from '../responses/public-box-visibility.response';
import { PublicBoxCategoryResponse } from '../responses/public-box-category.response';
import { PublicBoxAuthorizeRequest } from '../requests/public-box-authorize.request';
import { PublicBoxAuthorizeResponse } from '../responses/public-box-authorize.response';
import { PublicBoxDetailsResponse } from '../responses/public-box-details.response';
import { Nillable, valueOrDefault } from '@web/utils';
import { PublicBoxCosmeticCollectionItemResponse } from '../responses/public-box-cosmetic-collection-item.response';

@Injectable({
  providedIn: 'root',
})
export class PublicBoxApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public authorize(
    slug: string,
    access: PublicBoxAuthorizeRequest,
  ): Observable<PublicBoxAuthorizeResponse> {
    const path = `box/${HttpParams.Slug}/access-token`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Slug]: slug,
    });
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      access,
      PublicBoxAuthorizeRequest,
    );

    return this.provider.http
      .post(url, body, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(PublicBoxAuthorizeResponse),
        ),
      );
  }

  public checkVisibilityBySlug(
    slug: string,
    cacheable = false,
  ): Observable<PublicBoxVisibilityResponse> {
    const path = `box/${HttpParams.Slug}/status`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Slug]: slug,
    });
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(PublicBoxVisibilityResponse),
        ),
      );
  }

  public fetchBoxesCollection(options: {
    request: CollectionRequest;
    cacheable?: Nillable<boolean>;
  }): Observable<CollectionResponse<PublicBoxResponse>> {
    const path = 'box';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options.cacheable, false),
      )
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(PublicBoxResponse),
        ),
      );
  }

  public fetchCategoryCollection(
    request: CollectionRequest,
    cacheable = false,
  ): Observable<CollectionResponse<PublicBoxCategoryResponse>> {
    const path = 'dictionary/box-category';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByLoadingInterceptor()
      .skipByHttpErrorInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicBoxCategoryResponse,
          ),
        ),
      );
  }

  public fetchDetailsBySlug(
    slug: string,
    token?: Nillable<string>,
    cacheable = false,
  ): Observable<PublicBoxDetailsResponse> {
    const path = `box/${HttpParams.Slug}`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .queryParams({
          [HttpQueryParams.AccessToken]: token,
        })
        .params({
          [HttpParams.Slug]: slug,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(this.provider.httpHelper.bodyToResponse(PublicBoxDetailsResponse)),
      );
  }

  /* prettier-ignore */
  public fetchCosmeticCollectionBySlug(
 options: {
   slug: string,
   request: CollectionRequest,
   cacheable?: boolean,
 }
  ): Observable<CollectionResponse<PublicBoxCosmeticCollectionItemResponse>> {
    const {slug,cacheable, request} = options;
    const path = `box/${HttpParams.Slug}/cosmetic`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .path(path)
        .host(this.hostUrl())
        .params({ [HttpParams.Slug]: slug })
        .queryParams(this.provider.httpHelper.collectionRequestToQueryParams(request))
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByAuthTokenInterceptor()
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => valueOrDefault(cacheable, false))
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            PublicBoxCosmeticCollectionItemResponse,
          ),
        ),
      );
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
